import React, { type ReactElement, useCallback, useRef, useState } from 'react';

import Badge from '@atlaskit/badge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import {
	CheckboxOption,
	type FormatOptionLabelMeta,
	type InputActionMeta,
	type OptionType,
	PopupSelect,
	type PopupSelectProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';

import { type FilterOrControlId } from '../../../constants/filters/types';
import { mergeRefs } from '../../../utils/merge-refs';
import { FilterButton } from '../filter-button';
import { FilterButtonOld } from '../filter-button-old';
import { MaybeHoverOutWrapper } from '../hover-out-wrapper';
import { type CustomPopperModifier, matchReferenceWidthModifier } from '../utils/popper';

import { Control } from './control';
import {
	MenuListWithClearFilterOptionLabel,
	MenuListWithoutClearFilterOptionLabel,
} from './menu-list';
import { getButtonText } from './utils';

// The Lite app filter bar renders PopupSelect with a fully custom target, but it cannot import
// PopupSelect directly: the `@atlaskit/select` root barrel is on the prohibited-barrel ratchet, and
// the `/popup-select` subpath resolves to `undefined` under gemini-vr's atlaspack bundler (crashing
// the VR build). This module already imports PopupSelect from the root barrel, so expose it through
// a thin wrapper — a component definition, not a re-export, to satisfy `no-barrel-files`.
export function AppFilterPopupSelect<Option extends OptionType = OptionType>(
	props: PopupSelectProps<Option>,
): ReactElement {
	return <PopupSelect<Option> {...props} />;
}

type Options = OptionType[] | { label: string; options: OptionType[] }[];

export type SelectFilterOptionMetadata = { [key: string]: any };

const badgeStyles = xcss({
	alignItems: 'center',
	alignSelf: 'center',
	display: 'flex',
});

const neutralBackgroundStyles = xcss({
	backgroundColor: 'color.background.neutral',
	border: `${token('border.width')} solid transparent`,
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},
});

export type SelectFilterOption = {
	isSelected: boolean;
	trackingKey: string;
	value: string;
	queryParamValue: string;
	isSuggested: boolean;
	isDefault: boolean;
	avatarUrl?: string;
	label: string;
	metadata?: SelectFilterOptionMetadata;
	product?: string;
};

type SelectFilterProps = {
	defaultIsOpen?: boolean;
	isSearchable?: boolean;
	isLoading?: boolean;
	canClearFilter?: boolean;
	checkboxIndicators?: boolean;
	formatOptionLabel?: (
		data: OptionType,
		formatOptionLabelMeta: FormatOptionLabelMeta<OptionType>,
	) => React.ReactNode;
	placeholder?: string;
	searchInput?: string;
	onSearchInputChange?: (value: string) => void;
	onSelectChange: (value: SelectFilterOption | SelectFilterOption[]) => void;
	onDropdownOpen?: () => void;
	onDropdownClose?: () => void;
	options: Options;
	selectedOptions: SelectFilterOption[];
	label: string;
	noOptionsMessage?: string;
	filterOrControlId?: FilterOrControlId;
	getFirstSelectedLabel?: (selected: OptionType) => string;
	onClearOptions?: () => void;
	minMenuWidth?: number | string;
	testId?: string;
	iconBefore?: ReactElement | null;
	isFilterInDropdown?: boolean;
	hideOnHoverOut?: boolean;
	isFiltersDiscoverability2Enabled?: boolean;
	matchReferenceWidth?: boolean;
	contentFit?: 'default' | 'compact';
	filterOption?: (() => boolean) | null;
	// Overrides the chip's active styling so the filter does not look active but contains a non-removable default
	// e.g when the current site for multi-site filter is selected.
	isSelectedOverride?: boolean;
	// Renders the chip with a neutral grey background instead of the default subtle one.
	hasNeutralBackground?: boolean;
	// Renders each group heading; return a React node to render it richly (e.g. with an italic hint).
	formatGroupLabel?: (group: { label?: string; options: readonly OptionType[] }) => React.ReactNode;
};

const SelectFilter = ({
	defaultIsOpen = false,
	isSearchable = false,
	isLoading = false,
	canClearFilter = false,
	checkboxIndicators = false,
	formatOptionLabel,
	placeholder,
	searchInput,
	onSearchInputChange,
	onSelectChange,
	onDropdownOpen,
	onDropdownClose,
	options,
	selectedOptions,
	label,
	noOptionsMessage,
	filterOrControlId,
	getFirstSelectedLabel,
	onClearOptions,
	minMenuWidth,
	testId,
	iconBefore,
	isFilterInDropdown,
	isFiltersDiscoverability2Enabled,
	hideOnHoverOut,
	matchReferenceWidth = isFilterInDropdown,
	contentFit,
	filterOption: filterOptionOverride,
	isSelectedOverride,
	hasNeutralBackground,
	formatGroupLabel,
}: SelectFilterProps): React.JSX.Element => {
	const numSelectedOptions = selectedOptions.length;
	const firstSelected = selectedOptions[0];
	const filterButtonRef = useRef<HTMLButtonElement | null>(null);

	// We only use this state to control the open/close of the popup when hideOnHoverOut is enabled
	// Otherwise, the open/close is controlled by PopupSelect internally
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	// TODO: this can be removed if we have debounce in the store when lookup
	const [currentSearchInput, setCurrentSearchInput] = useState(searchInput);

	const customModifiers: CustomPopperModifier[] = [
		...(matchReferenceWidth ? [matchReferenceWidthModifier] : []),
	];

	const handleInputChange = useCallback(
		(value: string, action: InputActionMeta) => {
			if (action.action !== 'input-change') {
				return;
			}
			setCurrentSearchInput(value);

			if (!onSearchInputChange) {
				return;
			}

			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			debounceTimeout.current = setTimeout(() => {
				onSearchInputChange(value);
			}, 300);
		},
		[onSearchInputChange],
	);

	const handleChange = useCallback(
		(value: OptionType | readonly OptionType[]) => {
			onSelectChange(value as SelectFilterOption | SelectFilterOption[]);
		},
		[onSelectChange],
	);

	const handleClose = useCallback(() => {
		setIsPopupOpen(false);
		setCurrentSearchInput('');
		onDropdownClose?.();
	}, [onDropdownClose]);

	const handleOpen = useCallback(() => {
		setIsPopupOpen(true);
		onDropdownOpen?.();
	}, [onDropdownOpen]);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			if (event.relatedTarget !== filterButtonRef.current) {
				// Unblur is not from filter button
				handleClose();
			}
		},
		[handleClose],
	);

	const controlOpenStateProps = hideOnHoverOut &&
		fg('search_qf_close_dropdown_on_hover_off') && {
			isOpen: isPopupOpen,
			onBlur: handleBlur,
		};

	// Disable react-select's default client-side filtering so server results (e.g. matched by
	// project key) are not incorrectly filtered out because their label doesn't match the input.
	// Consumers can override this by passing filterOption={null} to preserve client-side filtering
	const filterOption =
		filterOptionOverride !== undefined
			? (filterOptionOverride ?? undefined)
			: isSearchable
				? () => true
				: undefined;

	return (
		<MaybeHoverOutWrapper callback={handleClose} enabled={hideOnHoverOut}>
			<PopupSelect
				{...controlOpenStateProps}
				placeholder={placeholder}
				onMenuOpen={handleOpen}
				onMenuClose={handleClose}
				minMenuWidth={minMenuWidth}
				maxMenuWidth="22rem"
				maxMenuHeight={400}
				menuPlacement="bottom"
				defaultIsOpen={defaultIsOpen}
				closeMenuOnSelect={!checkboxIndicators}
				isSearchable={isSearchable}
				searchThreshold={-1}
				blurInputOnSelect={false}
				isClearable={false}
				// @ts-ignore - https://product-fabric.atlassian.net/browse/DSP-21000
				defaultOptions={false}
				isLoading={isLoading}
				inputValue={currentSearchInput}
				onInputChange={handleInputChange}
				noOptionsMessage={currentSearchInput ? undefined : () => noOptionsMessage}
				canClearFilter={canClearFilter}
				onChange={handleChange}
				onClearOptions={onClearOptions}
				options={options}
				formatGroupLabel={formatGroupLabel}
				isOptionSelected={(option) => option.isSelected}
				shouldCloseMenuOnTab={false}
				label={label}
				filterOption={filterOption}
				popperProps={{
					strategy: 'fixed',
					modifiers: [
						{ name: 'offset', options: { offset: [0, 8] } },
						{
							name: 'preventOverflow',
							enabled: false,
						},
						...customModifiers,
					],
				}}
				target={({ isOpen, ...triggerProps }) => {
					let firstSelectedLabel = getFirstSelectedLabel
						? getFirstSelectedLabel(firstSelected)
						: firstSelected?.label;
					let labelElement: ReactElement | string = label;
					if (typeof label === 'string') {
						labelElement = getButtonText({
							label,
							firstSelectedLabel,
							numSelectedOptions,
						});
					}

					// `isSelectedOverride` is used so a filter can look neutral even while its dropdown is open.
					const isSelected = isSelectedOverride ?? (isOpen || numSelectedOptions > 0);

					if (isFiltersDiscoverability2Enabled) {
						return (
							<FilterButton
								{...(fg('search_qf_close_dropdown_on_hover_off') && {
									onClick: isOpen ? handleClose : handleOpen,
								})}
								{...triggerProps}
								ref={mergeRefs(triggerProps.ref, filterButtonRef)}
								testId={testId ? `${testId}-button` : undefined}
								isSelected={isSelected}
								iconBefore={iconBefore}
								filterOrControlId={filterOrControlId}
								isFilterInDropdown={isFilterInDropdown}
								contentFit={contentFit}
								xcss={hasNeutralBackground ? neutralBackgroundStyles : undefined}
								iconAfter={
									<>
										{numSelectedOptions > 1 ? (
											<Box xcss={badgeStyles}>
												<Badge appearance="information">{`+${numSelectedOptions - 1}`}</Badge>
											</Box>
										) : undefined}
									</>
								}
							>
								{labelElement}
							</FilterButton>
						);
					}
					return (
						<FilterButtonOld
							{...(fg('search_qf_close_dropdown_on_hover_off') && {
								onClick: isOpen ? handleClose : handleOpen,
							})}
							testId={testId ? `${testId}-button` : undefined}
							otherProps={triggerProps}
							isSelected={isSelected}
							label={labelElement}
							filterOrControlId={filterOrControlId}
							selectionBadge={
								numSelectedOptions > 1 ? (
									<Badge appearance="information">{`+${numSelectedOptions - 1}`}</Badge>
								) : undefined
							}
							iconBefore={iconBefore}
						/>
					);
				}}
				// formatOptionLabel and components are Removing in Q3 FY25
				// https://hello.atlassian.net/wiki/x/oQnE8g
				formatOptionLabel={formatOptionLabel}
				components={{
					Control,
					MenuList: isFiltersDiscoverability2Enabled
						? MenuListWithoutClearFilterOptionLabel
						: MenuListWithClearFilterOptionLabel,
					...(checkboxIndicators ? { Option: CheckboxOption } : {}),
				}}
			/>
		</MaybeHoverOutWrapper>
	);
};

export default SelectFilter;
